// C:\Users\imello\kq-frontend\src\components\tabs\rating\RecommendationsTab.jsx
import { useEffect, useMemo, useState } from 'react';
import { Circles } from 'react-loading-icons';
import { getNowPlayingMovie } from '../../../util/apis/MovieAPI';
import { getPlayingThisWeekTV } from '../../../util/apis/TvAPI';

function CardGrid({ items }) {
  if (!items?.length) {
    return (
      <div className="flex justify-center py-6">
        <Circles />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {items.map((item) => {
        const isMovie = Boolean(item.title);
        const title = isMovie ? item.title : item.name;
        const href = isMovie ? `/rate/movie/${item.id}` : `/rate/tv/${item.id}`;
        const img = item.poster_path
          ? `https://image.tmdb.org/t/p/original/${item.poster_path}`
          : '/placeholder.jpg';

        return (
          <a
            key={`${item.media_type || (isMovie ? 'movie' : 'tv')}-${item.id}`}
            href={href}
            className="bg-white rounded-xl p-3 shadow-md relative"
          >
            <span className="absolute top-3 left-3 -rotate-45 bg-secondary text-white py-0.5 px-3 rounded-lg text-xs">
              {isMovie ? 'Movie' : 'TV'}
            </span>
            <img
              src={img}
              alt={title}
              loading="lazy"
              className="rounded-lg aspect-poster object-cover w-full"
            />
            <p className="text-gray-800 font-semibold mt-2 text-center truncate">
              {title}
            </p>
          </a>
        );
      })}
    </div>
  );
}

function Section({ title, loading, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-2xl text-white font-semibold mb-3 border-b border-gray-500 pb-2">
        {title}
      </h2>
      {loading ? (
        <div className="flex justify-center py-6">
          <Circles />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

// Fisher–Yates shuffle for stable randomization
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function RecommendationsTab() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pull data exactly like WatchTab.jsx (movies + tv) then merge
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [movieData, tvData] = await Promise.all([
          getNowPlayingMovie.get(true),
          getPlayingThisWeekTV.get(true),
        ]);
        // Keep a modest number to keep UI snappy
        const movies = (movieData?.results || [])
          .slice(0, 20)
          .map((m) => ({ ...m, media_type: 'movie' }));
        const shows = (tvData?.results || [])
          .slice(0, 20)
          .map((t) => ({ ...t, media_type: 'tv' }));
        setContent([...movies, ...shows]);
      } catch (e) {
        console.error('RecommendationsTab fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 1) Most Rated — take top by vote_count, then shuffle and pick 5
  const mostRated = useMemo(() => {
    if (!content?.length) return [];
    const topPool = [...content]
      .sort((a, b) => (b?.vote_count ?? 0) - (a?.vote_count ?? 0))
      .slice(0, 20); // quality pool
    return shuffle(topPool).slice(0, 5); // randomize results shown
  }, [content]);

  // 2) Movie Suggestions — prefer higher vote_average (ties: poster presence), limit 5
  const movieSuggestions = useMemo(() => {
    const moviesOnly = (content || []).filter((x) => x.title);
    return [...moviesOnly]
      .sort((a, b) => {
        const va = a?.vote_average ?? 0;
        const vb = b?.vote_average ?? 0;
        if (vb !== va) return vb - va;
        const pa = a?.poster_path ? 1 : 0;
        const pb = b?.poster_path ? 1 : 0;
        return pb - pa;
      })
      .slice(0, 5);
  }, [content]);

  // 3) AI Recommended — simple client-side preference using genre_ids overlap; limit 5
  const aiRecommended = useMemo(() => {
    if (!content?.length) return [];

    let preferred = [];
    try {
      preferred = JSON.parse(localStorage.getItem('preferredGenres') || '[]');
    } catch {
      preferred = [];
    }
    const prefSet = new Set(preferred);

    const score = (it) => {
      const gids = Array.isArray(it?.genre_ids) ? it.genre_ids : [];
      const overlap = gids.filter((g) => prefSet.has(g)).length;
      const popBoost = Math.log10((it?.vote_count ?? 1) + 1);
      const qa = (it?.vote_average ?? 0) / 10; // normalize 0-1
      return overlap * 2 + popBoost * 0.75 + qa;
    };

    const ranked = [...content]
      .map((it) => ({
        it,
        s: prefSet.size ? score(it) : it?.vote_average ?? 0,
      }))
      .sort((a, b) => b.s - a.s)
      .map((x) => x.it)
      .slice(0, 5);

    return ranked;
  }, [content]);

  return (
    <div className="p-6">
      <Section title="Most Rated" loading={loading}>
        <CardGrid items={mostRated} />
      </Section>

      <Section title="Movie Suggestions" loading={loading}>
        <CardGrid items={movieSuggestions} />
      </Section>

      <Section title="AI Recommended" loading={loading}>
        <CardGrid items={aiRecommended} />
      </Section>
    </div>
  );
}
